import React, { useCallback, useEffect, useState } from 'react';
import { ReactFlow, MiniMap, Controls, Background, useNodesState, useEdgesState, addEdge, Handle } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { fetchAllWorkcells, fetchFactoryFloorPositions, saveFactoryFloorPositions } from '../api';
import getIconSrc from '../utils/getIconSrc';
import { useTranslation } from 'react-i18next';

export default function WorkcellFactoryPage() {
  const { t } = useTranslation();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [showMiniMap, setShowMiniMap] = useState(false);

  // Custom node that shows an icon (if available) and the label.
  // Assumptions: node.data.icon is a URL (absolute or relative to app/public) or null.
  function WorkcellNode({ data }) {
    const { label, icon } = data || {};
    const src = icon || getIconSrc(data) || '/placeholder-icon.svg';
  // console.debug('WorkcellNode resolved src', src, 'data:', data && { ...data });
    return (
      <div style={{ display: 'flex', alignItems: 'center', padding: 8, background: '#fff', border: '1px solid #ddd', borderRadius: 6, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', minWidth: 120 }}>
        <img
          src={src}
          alt={label || t('factoryFloor.icon')}
          style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, marginRight: 8 }}
          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/placeholder-icon.svg'; }}
        />
        <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
        {/* Keep connection handles so nodes stay connectable */}
        <Handle type="target" position="top" style={{ background: '#555' }} />
        <Handle type="source" position="bottom" style={{ background: '#555' }} />
      </div>
    );
  }

  const isWorkcellActive = useCallback((workcell) => {
    // Throughout the app, `has_oee` is used as the “active” flag for workcells.
    // Be tolerant to backend values like 0/1.
    if (!workcell) return false;
    const hasOee = workcell.has_oee;
    return hasOee === true || hasOee === 1 || hasOee === '1';
  }, []);

  const mapServerNodes = useCallback((serverNodes, allWorkcells) => {
    return (serverNodes || []).map((n) => {
      const nodeData = n.data || {};
      const wc = allWorkcells.find(w => w.workcell_id && String(w.workcell_id) === String(n.id));
      const iconFromNode = nodeData.icon;
      const icon = iconFromNode || getIconSrc(nodeData) || (wc ? getIconSrc(wc) : null);
      const label = nodeData.label || (wc && wc.workcell_name) || '';
      return {
        ...n,
        data: { ...nodeData, icon, label },
        type: n.type || (icon ? 'workcellNode' : 'default'),
      };
    });
  }, []);

  const loadDiagram = useCallback(async () => {
    setLoading(true);
    try {
      const allWorkcells = await fetchAllWorkcells();
      let serverData = null;
      try {
        serverData = await fetchFactoryFloorPositions();
      } catch (e) {}

      if (serverData && serverData.nodes) {
        setNodes(mapServerNodes(serverData.nodes || [], allWorkcells));
        setEdges((serverData.edges || []).map((e) => ({ ...e, type: e.type || 'smoothstep' })));
      } else {
        const activeWorkcells = allWorkcells.filter(isWorkcellActive);
        const nodes = activeWorkcells.map((wc, idx) => ({
          id: wc.workcell_id.toString(),
          data: { label: wc.workcell_name, icon: getIconSrc(wc) },
          type: getIconSrc(wc) ? 'workcellNode' : 'default',
          position: { x: 40 + idx * 180, y: 100 },
        }));
        setNodes(nodes);
        setEdges([]);
      }
    } catch (e) {
      setNodes([]);
      setEdges([]);
    }
    setLoading(false);
  }, [isWorkcellActive, mapServerNodes, setEdges, setNodes]);

  // Load workcells and diagram data
  useEffect(() => {
    loadDiagram();
  }, [loadDiagram]);

  // Save diagram changes to factory floor server endpoint
  const handleSave = async () => {
    setSaving(true);
    setSaveStatus('');
    try {
      await saveFactoryFloorPositions({ nodes, edges });
      setSaveStatus(t('factoryFloor.status.saved'));
    } catch (e) {
      setSaveStatus(t('factoryFloor.status.saveFailed'));
    }
    setSaving(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setSaveStatus('');
    try {
      const allWorkcells = await fetchAllWorkcells();
      const allWorkcellIds = new Set(
        allWorkcells
          .filter(w => w && w.workcell_id != null)
          .map(w => String(w.workcell_id))
      );
      const activeWorkcellIds = new Set(
        allWorkcells
          .filter(isWorkcellActive)
          .filter(w => w && w.workcell_id != null)
          .map(w => String(w.workcell_id))
      );

      const serverData = await fetchFactoryFloorPositions();
      const serverNodes = serverData?.nodes || [];
      const serverEdges = serverData?.edges || [];

      // Only prune nodes that correspond to known workcell IDs; keep unknown nodes intact.
      const prunedNodes = serverNodes.filter((n) => {
        const nodeId = String(n?.id);
        if (!allWorkcellIds.has(nodeId)) return true;
        return activeWorkcellIds.has(nodeId);
      });
      const keptNodeIds = new Set(prunedNodes.map(n => String(n.id)));

      const prunedEdges = serverEdges.filter((e) => {
        const sourceOk = e?.source == null || keptNodeIds.has(String(e.source));
        const targetOk = e?.target == null || keptNodeIds.has(String(e.target));
        return sourceOk && targetOk;
      });

      const removedCount = serverNodes.length - prunedNodes.length;

      if (removedCount > 0 || (serverEdges.length !== prunedEdges.length)) {
        await saveFactoryFloorPositions({ nodes: prunedNodes, edges: prunedEdges });
        setSaveStatus(t('factoryFloor.status.refreshedRemoved', { count: removedCount }));
      } else {
        setSaveStatus(t('factoryFloor.status.refreshedUnchanged'));
      }

      // Reload page state from the now-updated JSON.
      await loadDiagram();
    } catch (e) {
      setSaveStatus(t('factoryFloor.status.refreshFailed'));
    }
    setRefreshing(false);
  };

  const onConnect = useCallback((params) =>
    setEdges((eds) => addEdge({ ...params, type: 'smoothstep', animated: true }, eds)),
  [setEdges]);


  if (loading) return <div style={{ padding: 32 }}>{t('common.loading')}</div>;

  // register custom node types here
  const nodeTypes = { workcellNode: WorkcellNode, default: WorkcellNode };

  return (
    <div style={{ width: '100%', height: '85vh', background: '#fff', padding: "8px 32px", mx: 'auto' }}>
      <div style={{ marginBottom: 8, textAlign: 'right' }}>
        <span style={{ display: 'inline-block', marginLeft: 0 }}>{t('factoryFloor.title')}</span>
        <button
          onClick={handleRefresh}
          disabled={refreshing || loading || saving}
          style={{ padding: '8px 16px', fontWeight: 600, marginRight: 8 }}
        >
          {refreshing ? t('factoryFloor.refreshing') : t('factoryFloor.refresh')}
        </button>
        <button onClick={handleSave} disabled={saving} style={{ padding: '8px 16px', fontWeight: 600 }}>
          {saving ? t('factoryFloor.saving') : t('factoryFloor.save')}
        </button>
        <button
          onClick={() => setShowMiniMap((prev) => !prev)}
          style={{ padding: '8px 16px', fontWeight: 600, marginLeft: 8 }}
        >
          {showMiniMap ? t('factoryFloor.hideMiniMap') : t('factoryFloor.showMiniMap')}
        </button>
        {saveStatus && <span style={{ marginLeft: 12 }}>{saveStatus}</span>}
      </div>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        fitView
        defaultEdgeOptions={{ type: 'smoothstep', animated: true }}
        style={{ background: 'rgb(169, 166, 162)' }}
      >
        {showMiniMap && <MiniMap />}
        <Controls />
        <Background color="#aaa" gap={16} />
      </ReactFlow>
    </div>
  );
}
